import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronDown, ChevronRight, Menu, FileText, Award, Star } from "lucide-react";

const COURSE_CONTENT = {
  title: "Cloud & DevOps Engineer",
  modules: [
    {
      id: 1,
      title: "Module 1: Cloud Fundamentals",
      lessons: [
        { 
          id: 101, 
          title: "Introduction to AWS", 
          type: "text", 
          content: `
            <h2 class="text-2xl font-bold mb-4">Amazon Web Services</h2>
            <p>AWS offers reliable, scalable, and inexpensive cloud computing services.</p>
            <ul class="list-disc pl-5"><li>EC2 (Virtual Servers)</li><li>S3 (Storage)</li><li>Lambda (Serverless)</li></ul>
          ` 
        },
        { id: 102, title: "Virtualization & Containers", type: "text", content: "<h2>Docker Basics</h2><p>Containers package software into standardized units for development, shipment and deployment.</p>" },
        { id: 103, title: "Cloud Quiz", type: "quiz", questions: [{ q: "What is EC2?", options: ["Database", "Virtual Server", "DNS", "Storage"], correct: 1 }] }
      ]
    },
    {
      id: 2,
      title: "Module 2: DevOps Pipelines",
      lessons: [
        { id: 201, title: "CI/CD Concepts", type: "text", content: "<h2>Continuous Integration</h2><p>Automating the integration of code changes from multiple contributors.</p>" },
        { id: 202, title: "Jenkins & GitHub Actions", type: "text", content: "<p>Tools to automate your build, test, and deployment pipeline.</p>" },
        { id: 203, title: "DevOps Quiz", type: "quiz", questions: [{ q: "What does CI stand for?", options: ["Continuous Integration", "Code Intelligence", "Cloud Interface"], correct: 0 }] }
      ]
    }
  ]
};

function CourseCloud() {
  const navigate = useNavigate();
  const [activeModule, setActiveModule] = useState(1);
  const [activeLesson, setActiveLesson] = useState(COURSE_CONTENT.modules[0].lessons[0]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [quizScore, setQuizScore] = useState(null);

  const renderContent = () => {
    if (activeLesson.type === 'quiz') {
      return (
        <div className="max-w-2xl mx-auto bg-white p-8 rounded-2xl shadow-sm border border-gray-200">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2 text-gray-800"><Award className="text-orange-500"/> {activeLesson.title}</h2>
          {quizScore !== null ? (
             <div className="text-center py-10">
                <div className="text-4xl font-bold text-green-600 mb-2">{quizScore}%</div>
                <p className="text-gray-500">Assessment Complete</p>
                <button onClick={() => setQuizScore(null)} className="mt-4 text-blue-600 hover:underline">Retake</button>
             </div>
          ) : (
            <form onSubmit={(e) => { e.preventDefault(); setQuizScore(100); }}>
              {activeLesson.questions.map((q, i) => (
                <div key={i} className="mb-6">
                  <p className="font-bold text-gray-800 mb-3">{i+1}. {q.q}</p>
                  <div className="space-y-2">
                    {q.options.map((opt, idx) => (
                      <label key={idx} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                        <input type="radio" name={`q-${i}`} className="w-4 h-4 text-orange-600" />
                        <span className="text-sm text-gray-700">{opt}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              <button type="submit" className="w-full py-3 bg-gray-900 text-white rounded-xl font-bold hover:bg-orange-600 transition-colors">Submit Answers</button>
            </form>
          )}
        </div>
      );
    }
    return <div className="prose max-w-none text-gray-800" dangerouslySetInnerHTML={{ __html: activeLesson.content }} />;
  };

  return (
    <div className="h-screen w-screen flex flex-col bg-gray-50 font-sans overflow-hidden">
      <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 shrink-0 z-20">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/dashboard?tab=courses')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-500"><ArrowLeft size={20} /></button>
          <h1 className="text-base font-bold text-gray-800 hidden md:block">{COURSE_CONTENT.title}</h1>
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="md:hidden p-2 text-gray-500"><Menu size={20} /></button>
      </header>
      <div className="flex-1 flex overflow-hidden">
        <aside className={`bg-white border-r border-gray-200 w-80 shrink-0 flex flex-col transition-all duration-300 absolute md:relative h-full z-10 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0 md:w-0 md:opacity-0 md:overflow-hidden'}`}>
           <div className="p-4 border-b border-gray-100 bg-gray-50/50"><span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Curriculum</span></div>
           <div className="flex-1 overflow-y-auto">
              {COURSE_CONTENT.modules.map((module) => (
                 <div key={module.id} className="border-b border-gray-100">
                    <button onClick={() => setActiveModule(activeModule === module.id ? null : module.id)} className="w-full flex items-center justify-between p-4 bg-white hover:bg-gray-50 transition-colors text-left">
                       <span className="text-sm font-bold text-gray-700">{module.title}</span>
                       {activeModule === module.id ? <ChevronDown size={16} className="text-gray-400"/> : <ChevronRight size={16} className="text-gray-400"/>}
                    </button>
                    {activeModule === module.id && (
                       <div className="bg-gray-50/50">
                          {module.lessons.map((lesson) => (
                             <button key={lesson.id} onClick={() => setActiveLesson(lesson)} className={`w-full flex items-center gap-3 p-3 pl-6 text-left text-sm transition-colors border-l-2 ${activeLesson.id === lesson.id ? 'border-orange-500 bg-orange-50 text-orange-700 font-medium' : 'border-transparent text-gray-600 hover:bg-gray-100'}`}>
                                {lesson.type === 'quiz' ? <Star size={14}/> : <FileText size={14}/>} {lesson.title}
                             </button>
                          ))}
                       </div>
                    )}
                 </div>
              ))}
           </div>
        </aside>
        <main className="flex-1 overflow-y-auto bg-white p-8 md:p-12">
           <div className="max-w-4xl mx-auto">
              <div className="mb-8 border-b border-gray-100 pb-4">
                 <span className="text-orange-600 font-bold text-xs uppercase tracking-wider mb-1 block">{activeLesson.type === 'quiz' ? 'Assessment' : 'Lesson Content'}</span>
                 <h1 className="text-3xl font-bold text-gray-900">{activeLesson.title}</h1>
              </div>
              {renderContent()}
           </div>
        </main>
      </div>
    </div>
  );
}

export default CourseCloud;